import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-md)] text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-base)] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-[var(--color-blue)] text-white hover:bg-[#1d4ed8]",
        primary:
          "bg-[var(--color-navy)] text-white hover:bg-[#0b2249] shadow-[0_2px_16px_-2px_rgba(15,43,91,0.35)]",
        gold: "bg-[var(--color-gold)] text-[var(--color-navy)] hover:bg-[var(--color-gold-hover)]",
        destructive: "bg-[var(--color-danger)] text-white hover:bg-[#b91c1c]",
        outline:
          "border border-[var(--color-border-strong)] bg-transparent text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]",
        secondary: "bg-[var(--color-surface-2)] text-[var(--color-text-primary)] hover:opacity-80",
        ghost: "hover:bg-[var(--color-surface-2)]",
        link: "text-[var(--color-blue)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3",
        lg: "h-11 rounded-[var(--radius-xl)] px-6",
        xl: "h-13 rounded-[var(--radius-xl)] px-8 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
